/**
 * Error Boundary Component
 * Catches errors in child components and displays fallback UI
 */

import React, { Component, ReactNode } from 'react';
import { Button } from './Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-secondary px-4">
          <div className="w-full max-w-md">
            <div className="card text-center">
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="heading-2 mb-2">Something went wrong</h2>
              <p className="body mb-6 text-tertiary">
                We encountered an unexpected error. This has been logged and we'll look into it.
              </p>
              {this.state.error && (
                <div className="bg-error-50 border border-error-200 rounded-lg p-4 mb-6 text-left">
                  <p className="caption font-mono text-error-700">
                    {this.state.error.message}
                  </p>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Button variant="primary" onClick={this.handleReset} className="w-full">
                  Return to Dashboard
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => window.location.reload()}
                  className="w-full"
                >
                  Reload Page
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
